import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ApiService, { IntegrationLinkError } from '../services/apiService';
import { IntegrationPracticeLink, IntegrationUserLink } from '../types';
import { showAppToast } from './AppToastHost';
import { logger } from '../utils/logger';

/**
 * "Koppelingen" op de instellingenpagina: aan welke medewerker bij een partner
 * (bv. Praktijkdata) dit account hangt, en bij een zelfstandige praktijk zonder
 * organisatie ook de praktijkkoppeling op het eigen account; met ontkoppelen.
 * Rendert niets zolang er geen koppelingen zijn of de lijsten niet te laden zijn.
 */

// Eén sleutel voor bevestigen/verwijderen/fout, want id's van beide lijsten kunnen gelijk zijn
type RowKey = `user:${number}` | `practice:${number}`;

const IntegrationLinksSettings: React.FC = () => {
    const { t, i18n } = useTranslation();
    const apiService = useMemo(() => new ApiService(), []);
    const [links, setLinks] = useState<IntegrationUserLink[]>([]);
    const [practiceLinks, setPracticeLinks] = useState<IntegrationPracticeLink[]>([]);
    const [confirmingKey, setConfirmingKey] = useState<RowKey | null>(null);
    const [deletingKey, setDeletingKey] = useState<RowKey | null>(null);
    const [error, setError] = useState<{ key: RowKey; message: string } | null>(null);

    const load = useCallback(async () => {
        // Oudere servers kennen de endpoints niet; dan is er ook niets te tonen
        const [userResult, practiceResult] = await Promise.allSettled([
            apiService.getMyIntegrationLinks(),
            apiService.getMyPracticeLinks(),
        ]);
        if (userResult.status === 'rejected') {
            const loadError = userResult.reason;
            logger.warn('Koppelingen laden mislukt:', loadError instanceof IntegrationLinkError ? loadError.status : loadError);
        }
        if (practiceResult.status === 'rejected') {
            const loadError = practiceResult.reason;
            logger.warn('Praktijkkoppelingen laden mislukt:', loadError instanceof IntegrationLinkError ? loadError.status : loadError);
        }
        setLinks(userResult.status === 'fulfilled' ? userResult.value : []);
        setPracticeLinks(practiceResult.status === 'fulfilled' ? practiceResult.value : []);
    }, [apiService]);

    useEffect(() => {
        void load();
    }, [load]);

    // De medewerkerkoppeling van een eigen praktijk staat al in die praktijkregel,
    // en ontkoppelen van de praktijk wist hem mee
    const practiceConfigIds = new Set(practiceLinks.map((practice) => practice.configId));
    const ownUserLinks = links.filter((link) => !practiceConfigIds.has(link.configId));

    const titleFor = (link: IntegrationUserLink) =>
        link.configName ?? [link.partnerName, link.tenantName].filter(Boolean).join(' – ');

    const practiceTitleFor = (practice: IntegrationPracticeLink) =>
        practice.name || [practice.partnerName, practice.tenantName].filter(Boolean).join(' – ');

    const formatDate = (value: string) => {
        const date = new Date(value);
        return Number.isNaN(date.getTime())
            ? ''
            : date.toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' });
    };

    const remove = async (key: RowKey, request: () => Promise<void>, onRemoved: () => void) => {
        setDeletingKey(key);
        setError(null);
        try {
            await request();
            onRemoved();
            setConfirmingKey(null);
            showAppToast(t('userSettings.koppelingen.unlinked'));
        } catch (deleteError) {
            if (deleteError instanceof IntegrationLinkError && deleteError.status === 404) {
                // Al door iemand anders verwijderd
                setConfirmingKey(null);
                await load();
                return;
            }
            logger.error('Ontkoppelen mislukt:', deleteError instanceof IntegrationLinkError ? deleteError.status : deleteError);
            setError({ key, message: t('userSettings.koppelingen.unlinkError') });
        } finally {
            setDeletingKey(null);
        }
    };

    const unlink = (link: IntegrationUserLink) =>
        remove(`user:${link.id}`, () => apiService.deleteMyIntegrationLink(link.id), () => {
            setLinks((previous) => previous.filter((item) => item.id !== link.id));
        });

    const unlinkPractice = (practice: IntegrationPracticeLink) =>
        remove(`practice:${practice.configId}`, () => apiService.deleteMyPracticeLink(practice.configId), () => {
            setPracticeLinks((previous) => previous.filter((item) => item.configId !== practice.configId));
            setLinks((previous) => previous.filter((item) => item.configId !== practice.configId));
        });

    if (ownUserLinks.length === 0 && practiceLinks.length === 0) {
        return null;
    }

    const renderRow = (
        key: RowKey,
        title: string,
        details: React.ReactNode,
        question: string,
        onUnlink: () => void,
    ) => {
        const isConfirming = confirmingKey === key;
        const isDeleting = deletingKey === key;
        return (
            <li key={key} className="rounded-md border border-gray-200 bg-white px-3 py-3">
                <div className="flex flex-wrap items-center gap-3">
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900">{title}</p>
                        {details}
                    </div>
                    {!isConfirming && (
                        <button
                            type="button"
                            onClick={() => {
                                setError(null);
                                setConfirmingKey(key);
                            }}
                            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            {t('userSettings.koppelingen.unlink')}
                        </button>
                    )}
                </div>
                {isConfirming && (
                    <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5" role="alertdialog" aria-live="polite">
                        <p className="text-sm text-amber-900">{question}</p>
                        <div className="mt-3 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setConfirmingKey(null)}
                                disabled={isDeleting}
                                className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                type="button"
                                onClick={onUnlink}
                                disabled={isDeleting}
                                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50"
                            >
                                {isDeleting && (
                                    <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" aria-hidden="true"></span>
                                )}
                                {t('userSettings.koppelingen.unlink')}
                            </button>
                        </div>
                    </div>
                )}
                {error?.key === key && (
                    <p className="mt-2 text-xs text-red-600" role="alert">{error.message}</p>
                )}
            </li>
        );
    };

    return (
        <div className="mb-6">
            <div className="flex items-center mb-4">
                <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5M10.172 13.828a4 4 0 010-5.656l3-3a4 4 0 015.656 5.656l-1.5 1.5" />
                </svg>
                <h2 className="text-lg font-semibold text-gray-900">{t('userSettings.koppelingen.heading')}</h2>
            </div>

            <ul className="space-y-3">
                {practiceLinks.map((practice) => {
                    const title = practiceTitleFor(practice);
                    const praktijk = practice.tenantName ?? title;
                    const partner = practice.partnerName ?? title;
                    return renderRow(
                        `practice:${practice.configId}`,
                        title,
                        <>
                            <p className="mt-0.5 text-xs text-gray-500">
                                {t('userSettings.koppelingen.practiceRow', { praktijk, datum: formatDate(practice.linkedSince) })}
                                {!practice.isActive && ` · ${t('userSettings.koppelingen.inactive')}`}
                            </p>
                            <p className="mt-0.5 text-xs text-gray-500">
                                {practice.userLink
                                    ? (practice.userLink.displayName
                                        ? t('userSettings.koppelingen.practiceUser', { naam: practice.userLink.displayName })
                                        : t('userSettings.koppelingen.practiceUserNoName'))
                                    : t('userSettings.koppelingen.practiceNoUser', { partner })}
                            </p>
                        </>,
                        t('userSettings.koppelingen.practiceUnlinkQuestion', { praktijk, partner }),
                        () => void unlinkPractice(practice),
                    );
                })}
                {ownUserLinks.map((link) => {
                    const since = formatDate(link.linkedAt);
                    const partner = link.partnerName ?? titleFor(link);
                    return renderRow(
                        `user:${link.id}`,
                        titleFor(link),
                        <p className="mt-0.5 text-xs text-gray-500">
                            {link.displayName
                                ? t('userSettings.koppelingen.row', { naam: link.displayName, datum: since })
                                : t('userSettings.koppelingen.rowNoName', { datum: since })}
                        </p>,
                        t('userSettings.koppelingen.unlinkQuestion', { praktijk: link.tenantName ?? titleFor(link), partner }),
                        () => void unlink(link),
                    );
                })}
            </ul>
        </div>
    );
};

export default IntegrationLinksSettings;
